import React from "react";
import { Container, Box, Typography, Tabs, Tab } from "@mui/material";
import Footer from "../components/Footer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`puzzle-legal-tabpanel-${index}`}
      aria-labelledby={`puzzle-legal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PuzzleLegal: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}
        >
          Piece It! Legal
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={value} onChange={handleChange} centered>
            <Tab label="Terms & Conditions" />
            <Tab label="Privacy Policy" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: September 19, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Acceptance of Terms</Typography>
            <Typography variant="body1">
              By downloading, installing, or using Piece It! ("the App"), you
              agree to be bound by these Terms and Conditions. If you do not
              agree to these terms, you must uninstall the application
              immediately.
            </Typography>

            <Typography variant="h5">2. Description of Service</Typography>
            <Typography variant="body1">
              Piece It! is a jigsaw puzzle game in which players drag, snap, and
              complete colorful puzzle images. The App includes features such
              as:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Multiple puzzle images and levels with progressive unlocking</li>
              <li>Drag-and-snap tile gameplay with piece grouping</li>
              <li>Star ratings and best-time tracking per level</li>
              <li>Hint system and early level unlocks via rewarded ads</li>
              <li>Selectable themes, background music, and sound effects</li>
              <li>Ad-supported features</li>
            </Box>

            <Typography variant="h5">3. User Responsibilities</Typography>
            <Typography variant="body1">You agree to:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Use the App only for lawful, entertainment purposes</li>
              <li>
                Not attempt to reverse engineer, decompile, or disassemble the
                App
              </li>
              <li>
                Not use the App in any way that could damage, disable, or impair
                the App
              </li>
              <li>Comply with all applicable laws and regulations</li>
            </Box>

            <Typography variant="h5">4. Intellectual Property Rights</Typography>
            <Typography variant="body1">
              All text, graphics, icons, puzzle images, audio, and software code
              within this application are the exclusive property of the
              developers. You are granted a limited, non-exclusive,
              non-transferable license to use the App for personal,
              non-commercial purposes. You may not redistribute, reproduce, or
              use the App's assets as your own content.
            </Typography>

            <Typography variant="h5">5. Advertising</Typography>
            <Typography variant="body1">
              The App is ad-supported and displays advertisements through
              Google AdMob, including banner ads, interstitial ads shown between
              levels, and rewarded ads used to unlock hints or levels. By using
              the App, you agree to view these advertisements.
            </Typography>

            <Typography variant="h5">6. Disclaimers</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
              TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, OR NON-INFRINGEMENT.
            </Typography>
            <Typography variant="body1">We do not warrant that:</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>The App will be error-free or uninterrupted</li>
              <li>Defects will be corrected</li>
              <li>The App is free of viruses or other harmful components</li>
              <li>Results from using the App will be accurate or reliable</li>
            </Box>

            <Typography variant="h5">7. Limitation of Liability</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
              DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR
              OTHER INTANGIBLE LOSSES.
            </Typography>

            <Typography variant="h5">8. Changes to These Terms</Typography>
            <Typography variant="body1">
              We reserve the right to update our Privacy Policy and Terms and
              Conditions at any time. We will notify users of any changes by
              updating the "Last Updated" date at the top of this section.
            </Typography>

            <Typography variant="h5">9. Termination</Typography>
            <Typography variant="body1">
              We may terminate or suspend your access to the App immediately,
              without prior notice, for any reason, including if you breach
              these Terms and Conditions.
            </Typography>

            <Typography variant="h5">10. Governing Law</Typography>
            <Typography variant="body1">
              These Terms and Conditions shall be governed by and construed in
              accordance with the laws of your jurisdiction, without regard to
              its conflict of law provisions.
            </Typography>

            <Typography variant="h5">11. Contact Information</Typography>
            <Typography variant="body1">
              If you have any questions about these Terms and Conditions, please
              contact us at: sharmashashi940@gmail.com
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: September 19, 2026
          </Typography>

          <Box
            sx={{
              "& h5": { fontSize: "1.25rem", fontWeight: 600, mt: 3, mb: 1.5 },
              "& p, & ul": { lineHeight: 1.8, mb: 2 },
            }}
          >
            <Typography variant="h5">1. Introduction</Typography>
            <Typography variant="body1">
              Piece It! ("we," "our," or "us") is committed to protecting your
              privacy. This Privacy Policy describes how we handle information
              when you use our jigsaw puzzle application. We operate this app as
              an Ad-supported service provided at no cost and intended for use
              "AS IS."
            </Typography>

            <Typography variant="h5">
              2. Information Collection and Non-Collection
            </Typography>
            <Typography variant="body1">
              Our primary philosophy is data minimization.
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Personal Data:</strong> We <strong>do not</strong>{" "}
                collect any personally identifiable information (PII) such as
                your name, email address, physical address, or phone number. No
                user registration, login, or account is required.
              </li>
              <li>
                <strong>Account Deletion:</strong> Since no accounts are
                created, there is no personal data to delete.
              </li>
              <li>
                <strong>Local Data:</strong> All game data — level progress,
                star ratings, best times, unlocked levels, and app settings — is
                stored locally on your device using SharedPreferences and never
                leaves your device.
              </li>
              <li>
                <strong>Automatic Data Collection:</strong> Like most mobile
                applications, our third-party service providers (such as Google
                AdMob and Google Play Services) may automatically collect
                certain non-identifiable information, including device type,
                operating system, and basic usage statistics to improve service
                stability.
              </li>
            </Box>

            <Typography variant="h5">3. Advertising and Analytics</Typography>
            <Typography variant="body1">
              To keep our service free, we display advertisements provided by{" "}
              <strong>Google AdMob</strong> (banner ads, interstitial ads, and
              rewarded ads).
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                AdMob uses technologies like cookies or unique device
                identifiers to serve ads based on your interests.
              </li>
              <li>
                You can manage your ad preferences or opt-out of personalized
                ads via your Android device settings (
                <strong>
                  Settings &gt; Google &gt; Ads &gt; Opt out of Ads
                  Personalization
                </strong>
                ).
              </li>
              <li>
                For more information, please review the{" "}
                <a
                  href="https://policies.google.com/technologies/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  Google Advertising Privacy Policy
                </a>
                .
              </li>
            </Box>

            <Typography variant="h5">4. Data Retention and Deletion</Typography>
            <Typography variant="body1">
              You have complete control over your data:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Your progress and settings remain on your device only</li>
              <li>Uninstall the App to completely remove all local data</li>
              <li>Backing up your device will include Piece It! data</li>
            </Box>

            <Typography variant="h5">5. Security</Typography>
            <Typography variant="body1">
              We value your trust in using our app. We use industry-standard
              security measures to protect the integrity of the application.
              However, please be aware that no method of transmission over the
              internet or method of electronic storage is 100% secure.
            </Typography>

            <Typography variant="h5">6. Children's Privacy</Typography>
            <Typography variant="body1">
              Our services do not address anyone under the age of 13. We do not
              knowingly collect personally identifiable information from
              children. If we discover that a child under 13 has provided us
              with personal information (though our app has no such input
              fields), we immediately delete it from our systems.
            </Typography>

            <Typography variant="h5">7. Changes to This Privacy Policy</Typography>
            <Typography variant="body1">
              We may update this Privacy Policy from time to time. Changes will
              be posted at this page, and the "Last updated" date will be
              revised. Your continued use of the App after changes constitutes
              acceptance of the updated Privacy Policy.
            </Typography>

            <Typography variant="h5">8. Contact Us</Typography>
            <Typography variant="body1">
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us at: sharmashashi940@gmail.com
            </Typography>
          </Box>
        </TabPanel>
      </Container>
      <Footer />
    </Box>
  );
};

export default PuzzleLegal;